import { mappingContactsService } from "@/app/providers/services";
import { FlexLabel } from "@/components";
import { ContactsMappingModal } from "@/components/ContactsMappingModal";
import { ENABLE_UPDATE_ENTITY_REFERENCES } from "@/config/constants";
import { isFeatureEnabled } from "@/config/features";
import {
  CRUD_CREATE_FAILURE,
  CRUD_CREATE_SUCCESS,
  CRUD_GET_ONE_SUCCESS,
  CRUD_UPDATE_FAILURE,
  CRUD_UPDATE_SUCCESS,
  crudCreate,
  crudGetOne,
  crudUpdate,
} from "@/legacy/redux";
import { system } from "@/theme";
import { DecisionGraph } from "@imminently/decisively-core";
import Button from "@imminently/decisively-ui/Button";
import CheckboxField from "@imminently/decisively-ui/CheckboxField";
import Dialog from "@imminently/decisively-ui/Dialog";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import Form, { FieldWatch, useFormContext } from "@imminently/decisively-ui/Form";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import type { SelectOption } from "@imminently/decisively-ui/Select";
import SelectField from "@imminently/decisively-ui/SelectField";
import TextField from "@imminently/decisively-ui/TextField";
import Tooltip from "@mui/material/Tooltip";
import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import lowerCase from "lodash/lowerCase";
import omitBy from "lodash/omitBy";
import pick from "lodash/pick";
import snakeCase from "lodash/snakeCase";
import pluralize from "pluralize";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { z } from "zod";
import * as combinatorV2 from "../../../../redux/combinatorV2.saga";
import * as fullReleaseRedux from "../../../../redux/fullRelease/reducer";
import {
  ContactEntityHelperText,
  ContactIdAutocomplete,
  ContactIdHelperText,
  getAllAttributes,
  getReleaseMetaContact,
  trimSpacesInValues,
} from "./route/Entities/EditEntity_utils";

const NameFieldInner = ({
  initialTextSingular,
  textSingular,
  ...props
}: React.ComponentProps<typeof TextField> & { initialTextSingular: string; textSingular: string }) => {
  const form = useFormContext<AddEntityFormValues>();

  useEffect(() => {
    if (textSingular !== "" && initialTextSingular !== textSingular) {
      form.setValue(
        props.name as keyof AddEntityFormValues,
        pluralize(snakeCase(textSingular.replace("the ", ""))) as any,
      );
    }
  }, [textSingular, initialTextSingular, props.name, form]);

  return (
    <TextField
      {...props}
      fullWidth
    />
  );
};

const NameField = (props: React.ComponentProps<typeof TextField> & { initialTextSingular: string }) => (
  <FieldWatch<AddEntityFormValues> names={["textSingular"]}>
    {({ textSingular }) => (
      <NameFieldInner
        {...props}
        textSingular={textSingular ?? ""}
      />
    )}
  </FieldWatch>
);

const CollectiveFieldInner = ({
  initialTextSingular,
  textSingular,
  ...props
}: React.ComponentProps<typeof TextField> & { initialTextSingular: string; textSingular: string }) => {
  const form = useFormContext<AddEntityFormValues>();

  useEffect(() => {
    if (textSingular && textSingular !== "" && initialTextSingular !== textSingular) {
      form.setValue(
        props.name as keyof AddEntityFormValues,
        `all instances of ${lowerCase(pluralize(textSingular, 1))}` as any,
      );
    }
  }, [textSingular, initialTextSingular, props.name, form]);

  return (
    <TextField
      {...props}
      fullWidth
    />
  );
};

const CollectiveField = (props: React.ComponentProps<typeof TextField> & { initialTextSingular: string }) => (
  <FieldWatch<AddEntityFormValues> names={["textSingular"]}>
    {({ textSingular }) => (
      <CollectiveFieldInner
        {...props}
        textSingular={textSingular ?? ""}
      />
    )}
  </FieldWatch>
);

const getEntityWarning = (source: string, changeNotSupported?: boolean) => {
  if (changeNotSupported) {
    return "Changing the source entity is current unsupported. Please delete this entity and re-add if this is required";
  }

  if (source !== "" && source !== "global") {
    return 'Entities not on the global object may need their collective text to include the source entity name. Eg: "all instances of the possession for the household member"';
  }

  return "";
};

const isPossibleParent = (r: any, existing: any, relationships: any[]) => {
  const existingIsAncestorOfR = (function inner(temp: any) {
    if (existing === undefined) return false;
    if (temp.id === existing.id) return true;
    if (temp.source === "global") return false;

    const nextTemp = relationships.find((it) => it.target === temp.source);

    return nextTemp ? inner(nextTemp) : false;
  })(r);

  return existingIsAncestorOfR === false;
};

type ContainmentMode = "entity" | "relationship";

type RelationshipType = "OneToMany" | "OneToOne" | "ManyToOne" | "ManyToMany";
type RelationshipCardinality = "to-one" | "to-many";

export type AddEntityFormValues = {
  id?: string;
  name: string;
  reverseText: string;
  text: string;
  textSingular: string;
  containment: ContainmentMode;
  source: string;
  type: RelationshipType;
  createReverseRelationship: boolean;
  forwardCardinality: RelationshipCardinality;
  reverseCardinality: RelationshipCardinality;
  target: string;
  contactEntity: boolean;
  contactId: string;
};

type AddEntityProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  release: any;
  existing?: any;
  prefill?: Partial<AddEntityFormValues>;
};

const getRelationshipTextLabels = (relationshipType: RelationshipType) => {
  switch (relationshipType) {
    case "ManyToOne":
      return {
        forward: "Relationship Name (Singular)",
        reverse: "Reverse Relationship Name (Collective)",
      };
    case "OneToOne":
      return {
        forward: "Relationship Name (Singular)",
        reverse: "Reverse Relationship Name (Singular)",
      };
    case "ManyToMany":
      return {
        forward: "Relationship Name (Collective)",
        reverse: "Reverse Relationship Name (Collective)",
      };
    default:
      return {
        forward: "Relationship Name (Collective)",
        reverse: "Reverse Relationship Name (Singular)",
      };
  }
};

const getForwardCardinality = (relationshipType: RelationshipType): RelationshipCardinality =>
  relationshipType === "OneToOne" || relationshipType === "ManyToOne" ? "to-one" : "to-many";

const getReverseCardinality = (relationshipType: RelationshipType): RelationshipCardinality =>
  relationshipType === "OneToOne" || relationshipType === "OneToMany" ? "to-one" : "to-many";

const getInferredReverseCardinality = (forwardCardinality: RelationshipCardinality): RelationshipCardinality =>
  forwardCardinality === "to-one" ? "to-many" : "to-one";

const getRelationshipType = (
  forwardCardinality: RelationshipCardinality,
  reverseCardinality: RelationshipCardinality,
): RelationshipType => {
  if (forwardCardinality === "to-one" && reverseCardinality === "to-one") return "OneToOne";
  if (forwardCardinality === "to-one" && reverseCardinality === "to-many") return "ManyToOne";
  if (forwardCardinality === "to-many" && reverseCardinality === "to-many") return "ManyToMany";
  return "OneToMany";
};

const addIssueForBlankField = (
  ctx: z.RefinementCtx,
  values: AddEntityFormValues,
  field: keyof AddEntityFormValues,
  message = "Required",
) => {
  const value = values[field];
  if (typeof value === "string" && !value.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [field],
      message,
    });
  }
};

const getAddEntitySchema = (simplifiedRelationshipDialog: boolean) =>
  z
    .object({})
    .passthrough()
    .superRefine((rawValues, ctx) => {
      const values = rawValues as AddEntityFormValues;
      const isRelationship = values.containment === "relationship";

      addIssueForBlankField(ctx, values, "text");
      addIssueForBlankField(ctx, values, "source");

      if (isRelationship) {
        addIssueForBlankField(ctx, values, "target");
      } else {
        addIssueForBlankField(ctx, values, "textSingular");
        addIssueForBlankField(ctx, values, "name");
      }

      if (simplifiedRelationshipDialog && isRelationship && values.createReverseRelationship) {
        addIssueForBlankField(ctx, values, "reverseText", "Reverse relationship name is required");
      }

      if (values.name?.includes(" ")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["name"],
          message: "Spaces are not allowed",
        });
      }
    });

const AddEntity = ({ open, onOpenChange, release, existing, prefill }: AddEntityProps) => {
  const releaseId = release?.id;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showUpdateReferences, setShowUpdateReferences] = React.useState(false);
  const [updateReferences, setUpdateReferences] = React.useState(false);
  const simplifiedRelationshipDialog = isFeatureEnabled("experimental.simplified_relationship_dialog");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const schema = React.useMemo(() => getAddEntitySchema(simplifiedRelationshipDialog), [simplifiedRelationshipDialog]);

  const handleFieldChange = (
    fieldName: keyof AddEntityFormValues,
    value: string,
    initialValues: AddEntityFormValues,
    currentValues: AddEntityFormValues,
  ) => {
    const relevantFields: Array<keyof AddEntityFormValues> = ["name", "reverseText", "textSingular", "text"];

    if (relevantFields.includes(fieldName)) {
      const anyFieldChanged = relevantFields.some(
        (field) => ((field === fieldName ? value : currentValues[field]) || "") !== (initialValues[field] || ""),
      );
      setShowUpdateReferences(anyFieldChanged);
    }
  };

  const parsedGraph = React.useMemo(
    () =>
      release?.rule_graph ? DecisionGraph.from(release.rule_graph) : new DecisionGraph(),
    [release?.rule_graph],
  );

  const relationships = get(release, "relationships", []);
  const releaseMetaContact = getReleaseMetaContact(release);

  const entities = React.useMemo(() => {
    const nextEntities = [{ id: "global", name: "The Global Object" }];

    for (const r of relationships || []) {
      if (r.containment && isPossibleParent(r, existing, relationships)) {
        nextEntities.push({ id: r.target, name: r.textSingular });
      }
    }

    return nextEntities;
  }, [relationships, existing]);

  const closeDialog = React.useCallback(() => {
    setIsSubmitting(false);
    if (onOpenChange) {
      onOpenChange(false);
      return;
    }
  }, [onOpenChange, dispatch]);

  const getReleaseAndCloseAction = React.useMemo(
    () =>
      combinatorV2.ActionCreator({
        bootstrap: crudGetOne("releases", releaseId),
        success: {
          type: CRUD_GET_ONE_SUCCESS,
          validate: (v: any) => v.meta.resource === "releases",
          call: () => {
            dispatch(fullReleaseRedux.aCreators.requestToActualize());
            closeDialog();
          },
        },
      }),
    [releaseId, dispatch, closeDialog],
  );

  const isEdit = Boolean(existing);

  const allAttributes = React.useMemo(() => getAllAttributes(release), [release]);

  const [isContactMappingOpen, setIsContactMappingOpen] = React.useState(false);

  const defaultValues: AddEntityFormValues = {
    name: "",
    reverseText: "",
    text: "",
    textSingular: "",
    containment: "entity",
    source: "global",
    type: "OneToMany",
    createReverseRelationship: false,
    forwardCardinality: "to-many",
    reverseCardinality: getInferredReverseCardinality("to-many"),
    target: "",
    contactEntity: false,
    contactId: "",
  };

  const initialValues: AddEntityFormValues = existing
    ? {
        // do this before existing, in case it needs to be overridden
        updateReferences: false,
        ...existing,
        containment: existing.containment ? "entity" : "relationship",
        createReverseRelationship: Boolean(existing.reverseText),
        forwardCardinality: getForwardCardinality(existing.type || "OneToMany"),
        reverseCardinality: getReverseCardinality(existing.type || "OneToMany"),
      }
    : prefill
      ? { ...defaultValues, ...prefill }
      : defaultValues;

  const cardinalityOptions: SelectOption[] = [
    { value: "to-one", label: "To-one" },
    { value: "to-many", label: "To-many" },
  ];

  const containmentOptions: SelectOption[] = [
    { value: "entity", label: "Entity" },
    { value: "relationship", label: "Relationship" },
  ];

  const entityOptions: SelectOption[] = entities.map((entity) => ({
    value: entity.id,
    label: entity.name,
  }));

  const formContent = (
    <Form<AddEntityFormValues>
      key={existing?.id ?? `${prefill?.name ?? ""}-${prefill?.source ?? ""}-${prefill?.target ?? ""}`}
      defaultValues={initialValues}
      schema={schema as any}
      onSubmit={(valuesRaw) => {
        setIsSubmitting(true);
        const submissionValues = cloneDeep(valuesRaw) as AddEntityFormValues;
        const isRelationship = submissionValues.containment === "relationship";
        const values = { ...submissionValues, containment: !isRelationship } as Record<string, any>;
        if (simplifiedRelationshipDialog) {
          const effectiveReverseCardinality =
            isRelationship && !submissionValues.createReverseRelationship
              ? getInferredReverseCardinality(submissionValues.forwardCardinality)
              : submissionValues.reverseCardinality;
          values.type = getRelationshipType(values.forwardCardinality, effectiveReverseCardinality);
        }
        delete values.createReverseRelationship;
        delete values.forwardCardinality;
        delete values.reverseCardinality;

        if (simplifiedRelationshipDialog && isRelationship && !submissionValues.createReverseRelationship) {
          delete values.reverseText;
        }

        if (values.containment) {
          delete values.target;
          if (values.contactEntity === false) values.contactId = "";
        } else {
          delete values.textSingular;
          delete values.name;
          delete values.contactEntity;
          delete values.contactId;
        }

        if (isEdit) {
          delete values.containment;
          delete values.target;
        }

        if (updateReferences) {
          (values as any).updateReferences = true;
        }

        if (existing) {
          const payload = pick(cloneDeep(values), [
            "name",
            "nameSingular",
            "text",
            "textSingular",
            "reverseText",
            "source",
            "type",
            "containment",
            "target",
            "contactEntity",
            "contactId",
            "updateReferences",
          ]);

          // Empty reverseText: null signals deletion if one existed before, otherwise omit entirely
          if ("reverseText" in payload && !payload.reverseText) {
            if (existing.reverseText) {
              payload.reverseText = null;
            } else {
              delete payload.reverseText;
            }
          }

          dispatch(
            combinatorV2.ActionCreator({
              bootstrap: crudUpdate("releases", release.id, trimSpacesInValues(payload), {
                subResource: "relationships",
                id: values.id,
              }),
              success: {
                type: CRUD_UPDATE_SUCCESS,
                validate: (v: any) => v.meta.resource === "releases" && v.requestPayload.id === existing.id,
                dispatch: getReleaseAndCloseAction,
              },
              error: {
                type: CRUD_UPDATE_FAILURE,
                validate: (v: any) => v.meta.resource === "releases" && v.requestPayload.id === existing.id,
              },
              asFinally: {
                call: () => setIsSubmitting(false),
              },
            }),
          );
        } else {
          dispatch(
            combinatorV2.ActionCreator({
              bootstrap: crudCreate("releases", trimSpacesInValues(omitBy(values, (v) => v === "")), {
                subResource: "relationships",
                id: release.id,
              }),
              success: {
                type: CRUD_CREATE_SUCCESS,
                validate: (v: any) => v.meta.resource === "releases" && v.meta.subResource === "relationships",
                dispatch: getReleaseAndCloseAction,
              },
              error: {
                type: CRUD_CREATE_FAILURE,
                validate: (v: any) => v.meta.resource === "releases" && v.meta.subResource === "relationships",
              },
              asFinally: {
                call: () => setIsSubmitting(false),
              },
            }),
          );
        }
      }}
      className="contents"
    >
      {(form) => {
        const handleValueChange = (fieldName: keyof AddEntityFormValues) => (value: string) => {
          handleFieldChange(fieldName, value, initialValues, form.getValues());
        };

        const handleCardinalityChange =
          (fieldName: "forwardCardinality" | "reverseCardinality") => (value: string | null) => {
            if (value !== "to-one" && value !== "to-many") return;

            const values = form.getValues();
            const nextForwardCardinality = fieldName === "forwardCardinality" ? value : values.forwardCardinality;
            const nextReverseCardinality =
              fieldName === "reverseCardinality"
                ? value
                : values.createReverseRelationship
                  ? values.reverseCardinality
                  : getInferredReverseCardinality(nextForwardCardinality);

            form.setValue(fieldName, value);
            if (fieldName === "forwardCardinality" && !values.createReverseRelationship) {
              form.setValue("reverseCardinality", nextReverseCardinality);
            }
            form.setValue("type", getRelationshipType(nextForwardCardinality, nextReverseCardinality));
          };

        return (
          <FieldWatch<AddEntityFormValues>
            names={[
              "containment",
              "name",
              "type",
              "createReverseRelationship",
              "forwardCardinality",
              "reverseCardinality",
              "source",
              "contactEntity",
              "contactId",
            ]}
          >
            {(values) => {
              const isRelationship = values.containment === "relationship";
              const contactFieldsMode = (() => {
                if (isRelationship) return { type: "hide" } as const;

                if (typeof releaseMetaContact !== "string" || releaseMetaContact.length === 0) {
                  return { type: "editable" } as const;
                }

                if (values.name === releaseMetaContact) return { type: "editable" } as const;

                return { type: "anotherNodeIsContact" } as const;
              })();

              const relationshipLabels = getRelationshipTextLabels(values.type);
              const oldRelationshipOptions: SelectOption[] = [
                { value: "OneToMany", label: "OneToMany" },
                { value: "OneToOne", label: "OneToOne" },
              ];
              const oldExtendedRelationshipOptions: SelectOption[] = [
                ...oldRelationshipOptions,
                { value: "ManyToOne", label: "ManyToOne" },
              ];

              return (
                <>
                  <DialogHeader
                    title={isEdit
                      ? `Edit ${isRelationship ? "relationship" : "entity"}`
                      : `Add ${isRelationship ? "relationship" : "entity"}`}
                    controls={{ allowResize: false }}
                  />
                  <Dialog.Body asChild>
                    <FormPanel>
                      <SelectField
                        name="containment"
                        label="Type"
                        options={containmentOptions}
                        disabled={isEdit}
                        searchable={false}
                        fullWidth
                      />

                      {!isRelationship && (
                        <TextField
                          name="textSingular"
                          label="Entity Text (Singular)"
                          onValueChange={handleValueChange("textSingular")}
                          fullWidth
                        />
                      )}

                      <CollectiveField
                        name="text"
                        label={
                          simplifiedRelationshipDialog && isRelationship
                            ? relationshipLabels.forward
                            : "Relationship Name (Collective)"
                        }
                        initialTextSingular={initialValues.textSingular}
                        onValueChange={handleValueChange("text")}
                      />

                      {simplifiedRelationshipDialog && isRelationship ? (
                        <>
                          <SelectField
                            name="forwardCardinality"
                            label="Relationship"
                            options={cardinalityOptions}
                            disabled={isEdit}
                            onValueChange={handleCardinalityChange("forwardCardinality")}
                            fullWidth
                          />
                          <CheckboxField
                            name="createReverseRelationship"
                            label="Also create a reverse relationship"
                            onValueChange={(value) => {
                              form.setValue("createReverseRelationship", value);
                              if (!value) {
                                handleFieldChange("reverseText", "", initialValues, values);
                                form.setValue("reverseText", "");
                                const nextReverseCardinality = getInferredReverseCardinality(values.forwardCardinality);
                                form.setValue("reverseCardinality", nextReverseCardinality);
                                form.setValue(
                                  "type",
                                  getRelationshipType(values.forwardCardinality, nextReverseCardinality),
                                );
                              }
                            }}
                          />
                          {values.createReverseRelationship && (
                            <div className="flex flex-col gap-3 border-l border-gray-200 pl-4">
                              <TextField
                                name="reverseText"
                                label={relationshipLabels.reverse}
                                onValueChange={handleValueChange("reverseText")}
                                fullWidth
                              />
                              <SelectField
                                name="reverseCardinality"
                                label="Reverse relationship"
                                options={cardinalityOptions}
                                disabled={isEdit}
                                onValueChange={handleCardinalityChange("reverseCardinality")}
                                fullWidth
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        <TextField
                          name="reverseText"
                          label={
                            simplifiedRelationshipDialog
                              ? "Reverse Relationship Name (Singular)"
                              : "Reverse Text (Singular)"
                          }
                          onValueChange={handleValueChange("reverseText")}
                          fullWidth
                        />
                      )}

                      {!isRelationship && (
                        <NameField
                          name="name"
                          label="API Name"
                          initialTextSingular={initialValues.textSingular}
                          onValueChange={handleValueChange("name")}
                        />
                      )}

                      <SelectField
                        name="source"
                        label="Source"
                        options={entityOptions}
                        helperText={getEntityWarning(values.source)}
                        fullWidth
                      />

                      {isRelationship && (
                        <>
                          <SelectField
                            name="target"
                            label="Target"
                            options={entityOptions}
                            disabled={isEdit}
                            helperText={getEntityWarning(values.source, isEdit)}
                            fullWidth
                          />
                          {!simplifiedRelationshipDialog && (
                            <SelectField
                              name="type"
                              label="Relationship"
                              options={oldExtendedRelationshipOptions}
                              disabled={isEdit}
                              fullWidth
                            />
                          )}
                        </>
                      )}

                      {!isRelationship &&
                        (simplifiedRelationshipDialog ? (
                          <SelectField
                            name="forwardCardinality"
                            label="Relationship"
                            options={cardinalityOptions}
                            disabled={isEdit}
                            onValueChange={handleCardinalityChange("forwardCardinality")}
                            fullWidth
                          />
                        ) : (
                          <SelectField
                            name="type"
                            label="Relationship"
                            options={oldRelationshipOptions}
                            disabled={isEdit}
                            fullWidth
                          />
                        ))}

                      {(() => {
                        if (contactFieldsMode.type === "hide") return null;

                        const isDisabled = contactFieldsMode.type === "anotherNodeIsContact";
                        return (
                          <CheckboxField
                            name="contactEntity"
                            label={t("add_entity_modal.mark_as_contact_object")}
                            disabled={isDisabled}
                            helperText={
                              <ContactEntityHelperText
                                releaseHasActiveContact={contactFieldsMode.type === "anotherNodeIsContact"}
                                releaseMetaContact={releaseMetaContact}
                              />
                            }
                          />
                        );
                      })()}

                      {(() => {
                        if (
                          contactFieldsMode.type === "hide" ||
                          contactFieldsMode.type === "anotherNodeIsContact" ||
                          !values.contactEntity
                        ) {
                          return null;
                        }

                        const attributesForEntity = allAttributes.filter(
                          (it) => it.entity === values.name && it.type !== "meta",
                        );
                        const fieldsOnContactObject = attributesForEntity
                          .filter((it) => it.value !== values.contactId)
                          .map((it) => it.label);

                        return (
                          <div className="flex flex-col gap-3">
                            <div className="w-full">
                              <ContactIdAutocomplete
                                value={values.contactId || ""}
                                onChange={(id) => form.setValue("contactId", id)}
                                graph={parsedGraph}
                                entityName={values.name}
                              />
                              <ContactIdHelperText />
                            </div>

                            <Button
                              variant="contained"
                              intent="secondary"
                              onClick={() => setIsContactMappingOpen(true)}
                              label={t("add_entity_modal.mapping")}
                            />
                            <ContactsMappingModal
                              open={isContactMappingOpen}
                              onOpenChange={setIsContactMappingOpen}
                              fieldsOnContactObject={fieldsOnContactObject}
                              getMapping={() => mappingContactsService.getOne("contacts")}
                              save={(fields) =>
                                mappingContactsService.update({
                                  id: "contacts",
                                  payload: { release: releaseId, fields },
                                })
                              }
                            />
                          </div>
                        );
                      })()}

                      {showUpdateReferences && existing && ENABLE_UPDATE_ENTITY_REFERENCES && (
                        <div className="flex gap-2 items-center">
                          <CheckboxField
                            name="updateReferences"
                            label={t("add_entity_modal.update_references")}
                            value={updateReferences}
                            onValueChange={setUpdateReferences}
                          />
                          <Tooltip title="This feature is experimental and may not work as expected">
                            <FlexLabel
                              color={system.trafficAmber10}
                              style={{
                                color: system.trafficAmber,
                                border: `1px solid ${system.trafficAmber}`,
                              }}
                            >
                              <span>Experimental</span>
                            </FlexLabel>
                          </Tooltip>
                        </div>
                      )}

                      <div className="border-t border-gray-200" />
                    </FormPanel>
                  </Dialog.Body>
                  <Dialog.Actions>
                    <Dialog.Actions.CancelButton
                      intent="secondary"
                      label="Cancel"
                    />
                    <Dialog.Actions.PrimaryButton
                      label={isEdit ? "Save" : "Add"}
                      type="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    />
                  </Dialog.Actions>
                </>
              );
            }}
          </FieldWatch>
        );
      }}
    </Form>
  );

  if (typeof open === "boolean") {
    return (
      <Dialog
        intent="form"
        open={open}
        onOpenChange={onOpenChange}
      >
        <Dialog.Content className="overflow-hidden">{formContent}</Dialog.Content>
      </Dialog>
    );
  }

  return <Dialog.InlineRoot intent="form">{formContent}</Dialog.InlineRoot>;
};

export default AddEntity;
